import { DIAGRAM_COORDINATE_STEP, arrowGeometry, curvePoint, roundCoordinate, viaForMiddle, type DiagramElement, type DiagramPitch, type DiagramTeam } from '@ukinfo/shared';
import { fullView, screenToDrawing, type DiagramView, type ScreenRect } from './diagramView';
import { PITCH_HEIGHT } from './PitchDiagram';

/**
 * Reine Rechenhilfen des Skizzeneditors - ohne React, damit sie sich einzeln
 * pruefen lassen.
 *
 * Feldkoordinaten wie in der Skizze: x und y in Prozent des Spielfelds
 * (0 links/oben, 100 rechts/unten), Vielfache von 0,5 (ganze Zahlen bleiben gueltig).
 */

export type { ScreenRect };

export interface FieldPoint {
  x: number;
  y: number;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Koordinate im erlaubten Bereich 0 bis 100, auf das feinste Raster (Vielfache von 0,5) gerundet. */
export function toCoordinate(value: number): number {
  return clamp(roundCoordinate(value), 0, 100);
}

/** Auf ein Raster der Weite `step` gerundet, im Bereich 0 bis 100. */
export function snapToStep(value: number, step: number): number {
  const rounded = Math.round(value / step) * step;
  return clamp(rounded === 0 ? 0 : rounded, 0, 100);
}

/* ------------------------------------------------------------------ Raster */

/** Weite des Rasters in Feldprozent; ab diesem Zoom (und darueber) halb so weit. */
export const GRID_STEP = 1;
export const GRID_STEP_FINE = DIAGRAM_COORDINATE_STEP;
export const GRID_FINE_ZOOM = 3;
/** Kraeftigere Rasterlinien alle so viele Feldprozent; ab `GRID_MINOR_ZOOM` kommen feine Linien jede Einheit dazu. */
export const GRID_MAJOR = 5;
export const GRID_MINOR_ZOOM = 2;

/** Rasterweite beim Setzen und Ziehen: 1 Einheit, gezoomt ab 3x eine halbe. */
export function gridStepFor(zoom: number): number {
  return zoom >= GRID_FINE_ZOOM - 1e-6 ? GRID_STEP_FINE : GRID_STEP;
}

/**
 * Rechnet eine Bildschirmposition in eine Feldkoordinate um.
 *
 * Beruecksichtigt die viewBox samt Rand und die Ausrichtung des SVG
 * (`meet`, zentriert): Ist die Zeichnung schmaler als ihr Kasten, bleibt seitlich
 * Platz, und die Umrechnung stimmt trotzdem. Ist gezoomt, zeigt die viewBox nur einen
 * Ausschnitt (`view`); dann gilt die Position innerhalb dieses Ausschnitts. Positionen
 * ausserhalb des Feldes landen am naechsten Rand. Ohne `gridStep` auf Vielfache von 0,5 gerundet, mit eingeschaltetem
 * Raster auf dessen Weite.
 */
export function clientToField(
  clientX: number,
  clientY: number,
  rect: ScreenRect,
  pitch: DiagramPitch,
  view: DiagramView = fullView(pitch),
  gridStep: number | null = null,
): FieldPoint {
  const drawn = screenToDrawing(clientX, clientY, rect, pitch, view);
  if (!drawn) return { x: 0, y: 0 };
  const round = (value: number): number => (gridStep ? snapToStep(value, gridStep) : toCoordinate(value));
  return { x: round(drawn.x), y: round((drawn.y / PITCH_HEIGHT[pitch]) * 100) };
}

/** Abstand zweier Feldpunkte in Zeichnungseinheiten (x und y gleich gewichtet). */
export function fieldDistance(a: FieldPoint, b: FieldPoint, pitch: DiagramPitch): number {
  return Math.hypot(b.x - a.x, ((b.y - a.y) * PITCH_HEIGHT[pitch]) / 100);
}

/** Ab diesem Abstand gilt eine Geste als Ziehen, darunter als Tippen. */
export const DRAG_THRESHOLD = 4;

/** Naechste Nummer fuer einen neuen Spieler der Mannschaft: hoechste Zahl + 1. */
export function nextPlayerLabel(elements: readonly DiagramElement[], team: DiagramTeam): string {
  let highest = 0;
  for (const element of elements) {
    if (element.type !== 'player' || element.team !== team || !element.label) continue;
    if (/^\d+$/.test(element.label)) highest = Math.max(highest, Number(element.label));
  }
  return String(highest + 1);
}

export type GoalSize = 'MINI' | 'YOUTH' | 'FULL';

/** Passende Torgroesse zur Feldart: Minitor auf der freien Flaeche, Grosstor auf dem ganzen Feld. */
export function defaultGoalSize(pitch: DiagramPitch): GoalSize {
  return pitch === 'AREA' ? 'MINI' : pitch === 'HALF' ? 'YOUTH' : 'FULL';
}

/**
 * Drehung fuer ein neues Tor: Liegt es nah an einem Feldrand, oeffnet es sich
 * zum Feld hin (0 Grad: Rahmen ragt nach oben hinaus, am oberen Rand).
 */
export function goalRotationAt(point: FieldPoint, pitch: DiagramPitch): 0 | 90 | 180 | 270 {
  const height = PITCH_HEIGHT[pitch];
  const sy = (point.y / 100) * height;
  const edges = [
    { distance: sy, rotation: 0 as const },
    { distance: 100 - point.x, rotation: 90 as const },
    { distance: height - sy, rotation: 180 as const },
    { distance: point.x, rotation: 270 as const },
  ];
  const nearest = edges.reduce((best, edge) => (edge.distance < best.distance ? edge : best));
  return nearest.distance <= 12 ? nearest.rotation : 0;
}

type Zone = Extract<DiagramElement, { type: 'zone' }>;
type Arrow = Extract<DiagramElement, { type: 'arrow' }>;

/** Achsenparalleles Rechteck aus zwei gegenueberliegenden Ecken. */
export function zoneFromPoints(a: FieldPoint, b: FieldPoint): Zone {
  return { type: 'zone', x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
}

/** Verschiebt ein Element um (dx, dy), ohne dass es das Feld verlaesst. */
export function translateElement(element: DiagramElement, dx: number, dy: number): DiagramElement {
  switch (element.type) {
    case 'arrow': {
      // Kontrollpunkt und Knickpunkte wandern mit - sie muessen ebenfalls im Feld bleiben.
      const knicks = element.points ?? [];
      const xs = [element.from[0], element.to[0], ...(element.via ? [element.via[0]] : []), ...knicks.map((knick) => knick[0])];
      const ys = [element.from[1], element.to[1], ...(element.via ? [element.via[1]] : []), ...knicks.map((knick) => knick[1])];
      const mx = roundCoordinate(clamp(dx, -Math.min(...xs), 100 - Math.max(...xs)));
      const my = roundCoordinate(clamp(dy, -Math.min(...ys), 100 - Math.max(...ys)));
      const moved = { ...element, from: [element.from[0] + mx, element.from[1] + my] as [number, number], to: [element.to[0] + mx, element.to[1] + my] as [number, number] };
      const withVia = element.via ? { ...moved, via: [element.via[0] + mx, element.via[1] + my] as [number, number] } : moved;
      return element.points ? { ...withVia, points: element.points.map((knick): [number, number] => [knick[0] + mx, knick[1] + my]) } : withVia;
    }
    case 'measure': {
      const mx = roundCoordinate(clamp(dx, -Math.min(element.from[0], element.to[0]), 100 - Math.max(element.from[0], element.to[0])));
      const my = roundCoordinate(clamp(dy, -Math.min(element.from[1], element.to[1]), 100 - Math.max(element.from[1], element.to[1])));
      return { ...element, from: [element.from[0] + mx, element.from[1] + my], to: [element.to[0] + mx, element.to[1] + my] };
    }
    case 'zone': {
      const mx = roundCoordinate(clamp(dx, -element.x, 100 - element.x - element.w));
      const my = roundCoordinate(clamp(dy, -element.y, 100 - element.y - element.h));
      return { ...element, x: element.x + mx, y: element.y + my };
    }
    default:
      return { ...element, x: toCoordinate(element.x + dx), y: toCoordinate(element.y + dy) };
  }
}

/** Setzt Anfang oder Ende eines Pfeils oder einer Masslinie; ohne Laenge bleibt das Element unveraendert. */
export function moveArrowEnd<T extends { from: [number, number]; to: [number, number] }>(element: T, end: 'from' | 'to', point: FieldPoint): T {
  const next: [number, number] = [point.x, point.y];
  const other = end === 'from' ? element.to : element.from;
  if (next[0] === other[0] && next[1] === other[1]) return element;
  return { ...element, [end]: next };
}

/** Bis zu diesem Abstand (Zeichnungseinheiten) von der Geraden gilt ein gebogener Pfeil wieder als gerade. */
const STRAIGHT_SNAP = 1.2;

/** Der Pfeil ohne Biegung und ohne Knicke: `via`, `points` und `smooth` entfallen. */
export function straightenArrow(element: Arrow): Arrow {
  const { via: _via, points: _points, smooth: _smooth, ...rest } = element;
  return rest;
}

/** Mitte des Pfeils (bei t = 0,5) als Feldpunkt - dort sitzt der Griff zum Biegen. */
export function arrowMiddle(element: Extract<DiagramElement, { type: 'arrow' }>): { x: number; y: number } {
  return curvePoint(arrowGeometry(element), 0.5);
}

/**
 * Biegt den Pfeil so, dass seine Mitte an `point` liegt: Der Kontrollpunkt ist die
 * Spiegelung von `point` an der Mitte der Sehne, auf halbe Einheiten im Feld gerundet.
 * Liegt `point` (fast) auf der Geraden, bleibt der Pfeil gerade.
 */
export function bendArrow(
  element: Extract<DiagramElement, { type: 'arrow' }>,
  point: FieldPoint,
  pitch: DiagramPitch,
): Extract<DiagramElement, { type: 'arrow' }> {
  const { from, to } = arrowGeometry(element);
  const via = viaForMiddle(from, to, point);
  const chordMiddle = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  if (fieldDistance(via, chordMiddle, pitch) <= STRAIGHT_SNAP) return straightenArrow(element);
  const next: [number, number] = [toCoordinate(via.x), toCoordinate(via.y)];
  if (element.via && element.via[0] === next[0] && element.via[1] === next[1]) return element;
  return { ...element, via: next };
}

/** Radius der unsichtbaren Trefferflaeche in Zeichnungseinheiten bei 1x (7 sind rund 45 px Durchmesser am Handy). */
export const HIT_RADIUS = 7;
/** Der kleine Ball bekommt weniger, damit er danebenliegende Spieler nicht verdeckt. */
export const HIT_RADIUS_SMALL = { ball: 5, cone: 6, pole: 5 } as const;
/** Breite der unsichtbaren Trefferflaeche eines Pfeils bei 1x. */
export const HIT_STROKE = 8;
/** Breite der unsichtbaren Trefferflaeche am Rand einer Zone bei 1x. */
export const HIT_ZONE_STROKE = 4;

/**
 * Trefferflaechen und Griffe sollen auf dem Bildschirm etwa gleich gross bleiben, egal wie
 * stark gezoomt ist: In Zeichnungseinheiten werden sie durch den Zoom geteilt. Die Groesse
 * des Symbols spielt keine Rolle - kleine Symbole bleiben mit dem Finger genauso gut zu treffen.
 */
export function hitSize(base: number, zoom: number): number {
  return base / Math.max(zoom, 1);
}

/** Radius der Trefferflaeche eines Elements mit Mittelpunkt (nicht Pfeil und Zone). */
export function hitRadius(element: DiagramElement, zoom: number): number {
  const base = element.type === 'ball' || element.type === 'cone' || element.type === 'pole' ? HIT_RADIUS_SMALL[element.type] : HIT_RADIUS;
  return hitSize(base, zoom);
}

/* ------------------------------------------------------------------ Masslinie */

/** Breite eines Grossfelds in Metern - ganze und halbe Felder messen Meter wie das Spielfeld (Breite 68 m = 100 Einheiten). */
export const PITCH_WIDTH_METERS = 68;
/** Annahme fuer die Breite einer freien Flaeche in Metern, solange niemand etwas anderes eintraegt. */
export const AREA_WIDTH_DEFAULT_METERS = 30;

/** Laenge einer Strecke in Metern, wenn die Feldbreite `widthMeters` Meter misst. */
export function measureMeters(from: FieldPoint, to: FieldPoint, pitch: DiagramPitch, widthMeters: number): number {
  return (fieldDistance(from, to, pitch) * widthMeters) / 100;
}

/**
 * Text fuer einen Abstand: ab 10 m auf ganze Meter, darunter auf halbe Meter gerundet, mit Komma ("7,5 m").
 * Mindestens ein halber Meter, damit die Beschriftung nie leer ist.
 */
export function formatMeters(meters: number): string {
  const rounded = meters >= 10 ? Math.round(meters) : Math.max(0.5, Math.round(meters * 2) / 2);
  return `${String(rounded).replace('.', ',')} m`;
}
